import { useTranslation } from 'react-i18next'
import { Pencil } from 'lucide-react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/shadcn/table'
import { Button } from '@/components/shadcn/button'
import { LoaderTable } from '@/components/common/loaders/LoaderTable'
import type { EmployeeUserResponse, HealthInsuranceEmployeeConfigModel } from '@/api/model'

interface Props {
  employees: EmployeeUserResponse[]
  configsByEmployeeId: Map<string, HealthInsuranceEmployeeConfigModel>
  isLoading: boolean
  isError: boolean
  onRowClick?: (employee: EmployeeUserResponse) => void
  onEdit?: (employee: EmployeeUserResponse) => void
}

const COL_COUNT = 4

export function HealthInsuranceOverviewDataTable({
                                             employees,
                                             configsByEmployeeId,
                                             isLoading,
                                             isError,
                                             onRowClick,
                                             onEdit,
                                           }: Props) {
  const { t } = useTranslation(['service-modules-insurance', 'common'])

  return (
    <Table>
      <TableHeader className="sticky top-0 z-10 bg-card shadow-sm [&_th]:bg-card">
        <TableRow>
          <TableHead>{t('columns.firstName', { ns: 'common' })}</TableHead>
          <TableHead>{t('columns.lastName', { ns: 'common' })}</TableHead>
          <TableHead>{t('insurance.overview.insuranceNumber')}</TableHead>
          <TableHead className="w-10" />
        </TableRow>
      </TableHeader>
      <TableBody>
        <LoaderTable
          isLoading={isLoading}
          isError={isError}
          isEmpty={employees.length === 0}
          colSpan={COL_COUNT}
          errorMessage={t('insurance.overview.loadError')}
          emptyMessage={t('insurance.overview.empty')}
        >
          {employees.map((employee) => (
            <TableRow
              key={employee.id}
              onClick={onRowClick ? () => onRowClick(employee) : undefined}
              className={onRowClick ? 'cursor-pointer' : undefined}
            >
              <TableCell>{employee.firstName || '—'}</TableCell>
              <TableCell>{employee.lastName || '—'}</TableCell>
              <TableCell>
                {configsByEmployeeId.get(employee.id ?? '')?.healthInsuranceNumber || '—'}
              </TableCell>
              <TableCell onClick={(e) => e.stopPropagation()}>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={t('insurance.overview.edit')}
                  title={t('insurance.overview.edit')}
                  onClick={() => onEdit?.(employee)}
                >
                  <Pencil className="size-4 text-primary" />
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </LoaderTable>
      </TableBody>
    </Table>
  )
}
